import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { nextTick, onMounted, ref } from 'vue';
import { gsap } from 'gsap';
import SplitText from 'gsap/SplitText';
import Viewport from '@/store/modules/Viewport';


export default /*#__PURE__*/_defineComponent({
  __name: 'LetterByLetter',
  props: {
  debug: {
    type: Boolean,
    default: false
  },
  tag: {
    type: String,
    default: 'div'
  },
  overflow: {
    type: Boolean,
    default: false
  },
  auto: {
    type: Boolean,
    default: true
  },
  repeat: {
    type: Number,
    default: 0
  },
  delay: {
    type: Number,
    default: 0
  },
  duration: {
    type: Number,
    default: 0.25
  },
  stagger: {
    type: [Number, Function],
    default: 0.025
  },
  animated: {
    type: Boolean,
    default: true
  },
  letterSpacingAnimated: {
    type: Boolean,
    default: false
  },
  randomize: {
    type: Boolean,
    default: false
  },
  useLine: {
    type: Boolean,
    default: false
  }
},
  setup(__props) {

const props = __props;

gsap.registerPlugin(SplitText);



const textSplited = ref<SplitText>(null)
const textSplitedParent = ref<SplitText>(null)
const oldWindowWidth = ref<number>(0)
const root = ref<HTMLElement>()
const init = ref<boolean>(false)
const played = ref<boolean>(false)

onMounted(() => {
  onResize()

  if (props.animated) {
    if (props.auto) onEnter()
  }
})


const randomize = (prohibited: Array<string> = []): string => {
  let characters       = 'abcdefghijklmnopqrstuvwxyz';
  prohibited.forEach((p) => {
    characters = characters.replace(p, '')
  })
  const charactersLength = characters.length;

  return characters.charAt(Math.floor(Math.random() * charactersLength));
}


const onResize = async() => {
  if (Viewport.windowWidth === oldWindowWidth.value) return
  
  oldWindowWidth.value = Viewport.windowWidth

  root.value.style.height = null

  if (textSplited.value || textSplitedParent.value) {
    textSplitedParent.value.revert()
    textSplited.value.revert()
  }

  await nextTick()
  
  textSplited.value = new SplitText(root.value, { type: props.useLine ? 'chars,words,lines' : 'chars,words', charsClass: 'Letter', wordsClass: 'Word', linesClass: 'Line Line--++' });
  
  if (props.overflow) {
    textSplitedParent.value = new SplitText(root.value, { type: 'chars', charsClass: 'Letter__Parent' });
  }

  if (props.randomize) {
    textSplited.value.chars.forEach((char) => {
      const c = (char as HTMLElement).innerHTML;
      const r1 = randomize([c]);
      const r2 = randomize([c, r1]);
      (char as HTMLElement).innerHTML = `<span class="Letter__Base">${ c }</span><span class="Letter__Random Letter__Random--2">${ r1 }</span><span class="Letter__Random Letter__Random--1">${ r2 }</span>`
    })
  }

  init.value = true
  if (props.animated) onEnter()
}

const onEnter = () => {
  const tl = gsap.timeline({ delay: props.delay, repeat: props.repeat });

  if (init.value && !played.value) {
    played.value = true

    const $ = gsap.utils.selector(root.value)
    tl
      .to([$('.Letter')], {
        opacity: 1,
        stagger: props.stagger as number,
        duration: props.duration,
        ease: 'elastic.out(1, 0.3)'
      }, 'start')
      .to([$('.Letter')], { opacity: 0, duration: 0.04, ease: 'elastic.out(1, 0.3)' })
      .to([$('.Letter')], { opacity: 1, duration: 0.04,  ease: 'elastic.out(1, 0.3)' })
      .to([$('.Letter')], { opacity: 0, duration: 0.04, ease: 'elastic.out(1, 0.3)' })
      .to([$('.Letter')], { opacity: 1, duration: 0.04,  ease: 'elastic.out(1, 0.3)' })
      
      if(props.letterSpacingAnimated) {
        gsap.set(root.value, { display: 'flex' })
        gsap.set($('.Word'), { display: 'flex', flexWrap: 'no-wrap', gap: "1rem", marginRight: "0.5rem" })
        gsap.to($('.Word'), {
          gap: 0,
          duration: 0.5,
          delay: 0.3,
          ease: 'power2.out'
        })
      }

    if (props.randomize) {
      
      tl.set([$('.Letter__Random--1')], { display: 'inline-block' }, 0);
      tl.set([$('.Letter__Random--2, .Letter__Base')], { display: 'none' }, 0);

      $('.Letter').forEach((letter, index) => {
        const $letter = gsap.utils.selector(letter)
        const startAt = index * (props.stagger as number)

        tl.set(
          $letter('.Letter__Random--1'), 
          { display: 'none', immediateRender: false }, 
          startAt + (props.stagger as number)
        )
        tl.set(
          $letter('.Letter__Random--2'), 
          { display: 'inline-block', immediateRender: false }, 
          startAt + (props.stagger as number)
        )

        tl.set(
          $letter('.Letter__Random--2'), 
          { display: 'none', immediateRender: false }, 
          startAt + (props.stagger as number * 2)
        )
        tl.set(
          $letter('.Letter__Base'), 
          { display: 'inline-block', immediateRender: false }, 
          startAt + (props.stagger as number * 2)
        )
      })


      
    }
  }

  return tl
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(__props.tag), {
    ref_key: "root",
    ref: root
  }, {
    default: _withCtx(() => [
      _renderSlot(_ctx.$slots, "default")
    ]),
    _: 3
  }, 512))
}
}

})